import { Banknote, Receipt, TrendingUp, ChevronRight, ChartColumn, Flame } from 'lucide-react';
import clx from 'classnames';
import HoveredInfoIcon from '../../../Common/HoveredInfoIcon';
import DeltaBadge from './DeltaBadge';
import { HomeMetricValueSkeleton } from '../HomeSkeletons';
import styles from './HomeInfoCards.module.scss';

const METRIC_ICONS = {
  revenue: Banknote,
  avgTicket: Receipt,
  profit: TrendingUp,
};

const isPendingValue = (value) => value === '…' || value === '...' || value == null;

const PerformanceCard = ({
  card,
  stopBubbleProps,
  compact = false,
  isPremiumUnlocked = true,
  onUpgrade,
  premiumLabel,
  canLoadInsights = false,
  canHideInsights = false,
  onLoadInsights,
  onHideInsights,
  loadInsightsLabel,
  hideInsightsLabel,
  onOpenStats,
}) => {
  const [hero, ...secondary] = card.values;

  return (
    <div className={`${styles.performance_card_inner} ${styles.performance_theme}`}>
      <div className={styles.performance_header}>
        <div className={styles.performance_header_icon}>
          <ChartColumn size={20} strokeWidth={2.4} />
        </div>
        <div className={styles.performance_header_text}>
          <span className={styles.performance_title}>{card.cardName}</span>
          <span className={styles.performance_subtitle}>{card.subtitle}</span>
        </div>
      </div>

      {hero ? (
        <div className={styles.performance_hero} {...(canLoadInsights ? stopBubbleProps : {})}>
          <div className={styles.performance_hero_top}>
            <span className={styles.performance_metric_label}>{hero.title}</span>
            {!compact ? (
              <div {...stopBubbleProps}>
                <HoveredInfoIcon description={hero.infoDescription} />
              </div>
            ) : null}
          </div>
          <div className={styles.performance_hero_row}>
            {isPendingValue(hero.count) ? (
              <HomeMetricValueSkeleton wide />
            ) : (
              <span className={styles.performance_hero_value}>{hero.count}</span>
            )}
            {canLoadInsights && loadInsightsLabel ? (
              <button
                type="button"
                className={styles.performance_load_cta}
                onClick={(event) => {
                  event.stopPropagation();
                  onLoadInsights?.();
                }}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {loadInsightsLabel}
              </button>
            ) : null}
            {canHideInsights && hideInsightsLabel ? (
              <button
                type="button"
                className={styles.performance_load_cta}
                onClick={(event) => {
                  event.stopPropagation();
                  onHideInsights?.();
                }}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {hideInsightsLabel}
              </button>
            ) : null}
            {!isPendingValue(hero.count) && !canLoadInsights && !canHideInsights ? (
              <DeltaBadge
                value={hero.value}
                isPercent={hero.isPercent}
                positive={hero.positive}
                hint={hero.deltaHint}
                stopBubbleProps={stopBubbleProps}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={styles.performance_grid}>
        {secondary.map((metric) => {
          const Icon = METRIC_ICONS[metric.id] || Receipt;
          const isProfitLocked = metric.id === 'profit' && !isPremiumUnlocked;

          return (
            <div
              key={metric.title}
              className={clx(styles.performance_tile, {
                [styles.performance_tile_premium]: isProfitLocked,
              })}
            >
              {isProfitLocked ? (
                <>
                  <div className={styles.performance_tile_locked_mock} aria-hidden="true">
                    <div className={styles.performance_tile_head}>
                      <span className={styles.performance_tile_icon}>
                        <Icon size={14} strokeWidth={2.25} />
                      </span>
                      <span className={styles.performance_metric_label}>{metric.title}</span>
                    </div>
                    <div className={styles.performance_tile_row}>
                      <span className={styles.performance_tile_value}>€•••</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.performance_tile_lock}
                    onClick={(event) => {
                      event.stopPropagation();
                      onUpgrade?.();
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                    aria-label={premiumLabel}
                  >
                    <span className={styles.performance_tile_lock_card}>
                      <span className={styles.performance_tile_lock_icon} aria-hidden="true">
                        <Flame size={12} strokeWidth={2.4} fill="currentColor" />
                      </span>
                      <span className={styles.performance_tile_lock_title}>{premiumLabel}</span>
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.performance_tile_head}>
                    <span className={styles.performance_tile_icon}>
                      <Icon size={14} strokeWidth={2.25} />
                    </span>
                    <span className={styles.performance_metric_label}>{metric.title}</span>
                    {!compact ? (
                      <div {...stopBubbleProps}>
                        <HoveredInfoIcon description={metric.infoDescription} />
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.performance_tile_row}>
                    {isPendingValue(metric.count) ? (
                      <HomeMetricValueSkeleton />
                    ) : (
                      <span className={styles.performance_tile_value}>{metric.count}</span>
                    )}
                    {!isPendingValue(metric.count) ? (
                      <DeltaBadge
                        value={metric.value}
                        isPercent={metric.isPercent}
                        positive={metric.positive}
                        hint={metric.deltaHint}
                        stopBubbleProps={stopBubbleProps}
                      />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {card.footerCta ? (
        <button
          type="button"
          className={styles.performance_footer}
          onClick={(event) => {
            event.stopPropagation();
            onOpenStats?.();
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <span>{card.footerCta.replace(/\s*→\s*$/, '')}</span>
          <ChevronRight size={14} strokeWidth={2.5} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
};

export default PerformanceCard;
